<template>
  <div class="suscripcion-container">
    <!-- HEADER -->
    <div class="top-bar">
      <button class="btn-back" @click="goBack">
        <i class="material-icons">arrow_back</i>
      </button>
      <h1>Mi Suscripción</h1>
    </div>

    <!-- SECCIÓN DE USUARIO CON SKELETON -->
    <div v-if="loading" class="user-info skeleton">
      <div class="user-avatar skeleton-avatar"></div>
      <div class="user-details">
        <div class="skeleton-text skeleton-title"></div>
        <div class="skeleton-text skeleton-email"></div>
      </div>
    </div>
    <div v-else-if="user && user.id" class="user-info">
    <div class="user-avatar">
      <img 
        :src="user.avatar && user.avatar !== 'avatar.png' ? user.avatar : '/avatar.png'" 
        :alt="user.full_name || user.first_name || 'Usuario'"
        @error="handleImageError"
      >
    </div>
    <div class="user-details">
      <h3>{{ user.full_name || (user.first_name + ' ' + user.last_name) || 'Usuario' }}</h3>
      <p>{{ user.email || 'Email no disponible' }}</p>
    </div>
  </div>

    <!-- Skeleton Loader para el contenido principal -->
    <div v-if="loading" class="skeleton-container">
      <!-- Skeleton para el card principal -->
      <div class="card skeleton-card">
        <div class="skeleton-badge"></div>
        <div class="skeleton-text skeleton-heading"></div>
        <div class="skeleton-details">
          <div class="skeleton-detail-item"></div>
          <div class="skeleton-detail-item"></div>
          <div class="skeleton-detail-item"></div>
        </div>
        <div class="skeleton-button"></div>
        <div class="skeleton-benefits">
          <div class="skeleton-benefit-item"></div>
          <div class="skeleton-benefit-item"></div>
          <div class="skeleton-benefit-item"></div>
          <div class="skeleton-benefit-item"></div>
        </div>
      </div>
    </div>

    <template v-else>
      <!-- Mensaje de éxito después del pago -->
      <div v-if="paymentSuccess" class="alert success">
        <i class="material-icons">check_circle</i>
        <p>¡Pago exitoso! Tu suscripción premium está activa.</p>
      </div>

      <!-- Mensaje de error -->
      <div v-if="errorMessage" class="alert error">
        <i class="material-icons">error</i>
        <p>{{ errorMessage }}</p>
        <button class="close-alert" @click="errorMessage = null">
          <i class="material-icons">close</i>
        </button>
      </div>

      <!-- USUARIO PREMIUM -->
      <div v-if="subscriptionStatus?.is_premium" class="card premium">
        <div class="premium-badge">
          <i class="material-icons">verified</i>
          <span>Plan Activo</span>
        </div>
        
        <h2>¡Gracias por ser Premium!</h2>
        
        <div class="subscription-details">
          <div class="detail-item">
            <i class="material-icons">event</i>
            <div>
              <label>Estado</label>
              <p class="active">Activa</p>
            </div>
          </div>
          
          <div class="detail-item" v-if="subscriptionStatus.subscription_expires_at">
            <i class="material-icons">schedule</i>
            <div>
              <label>Vence el</label>
              <p>{{ formatDate(subscriptionStatus.subscription_expires_at) }}</p>
            </div>
          </div>
          
          <div class="detail-item" v-if="subscriptionStatus.days_remaining > 0">
            <i class="material-icons">hourglass_top</i>
            <div>
              <label>Días restantes</label>
              <p :class="{ warning: subscriptionStatus.days_remaining <= 7 }">
                {{ subscriptionStatus.days_remaining }} días
              </p>
            </div>
          </div>
        </div>

        <div class="trial-info" v-if="subscriptionStatus.is_on_trial">
          <i class="material-icons">science</i>
          <span>Período de prueba: {{ subscriptionStatus.trial_days_remaining }} días restantes</span>
        </div>

        <button @click="cancelSubscription" class="btn-cancel" :disabled="cancelling">
          {{ cancelling ? 'Procesando...' : 'Cancelar Suscripción' }}
        </button>

        <h3>Beneficios de la Suscripción</h3>
        <ul class="benefits-list">
          <li v-for="(item, index) in benefits" :key="index">
            <i class="material-icons">check_circle</i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>

      <!-- USUARIO NO PREMIUM -->
      <div v-else class="card free">
        <div class="free-badge">
          <i class="material-icons">lock</i>
          <span>Cuenta Gratuita</span>
        </div>
        
        <h2>🚫 Sin Suscripción Activa</h2>
        <p>Actualmente no tienes acceso al contenido premium</p>
        
        <div class="pricing-card">
          <div class="price">
            <span class="currency">$</span>
            <span class="amount">{{ subscriptionConfig?.price || '2.99' }}</span>
            <span class="period">/ mes</span>
          </div>
        </div>

        <button @click="initiatePayment" class="btn-pay" :disabled="paymentProcessing">
          <span v-if="!paymentProcessing">
            <i class="material-icons">payment</i> Pagar con tarjeta
          </span>
          <span v-else class="btn-spinner"></span>
        </button>

        <p class="secure-payment">
          <i class="material-icons">security</i>
          Pago 100% seguro
        </p>

        <h3>Beneficios Premium</h3>
        <ul class="benefits-list">
          <li v-for="(item, index) in benefits" :key="index">
            <i class="material-icons">check_circle</i>
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </template>
  </div>
  <!-- MODAL CANCELAR SUSCRIPCIÓN -->
<div v-if="showCancelModal" class="modal-overlay">
  <div class="modal">
    <h3>Cancelar Suscripción</h3>
    <p>¿Deseas cancelar la renovación automática? Seguirás siendo premium hasta el fin del periodo.</p>

    <div class="modal-actions">
      <button class="btn-secondary" @click="showCancelModal = false">
        No, mantener
      </button>

      <button class="btn-danger" @click="confirmCancel">
        Sí, cancelar
      </button>
    </div>
  </div>
</div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../../services/api'

const router = useRouter()
const route = useRoute()

// 🔥 NUEVO
const showCancelModal = ref(false)

const toast = ref({
  show: false,
  message: '',
  type: 'success'
})

const showToast = (message, type = 'success') => {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

// Estados
const loading = ref(true)
const paymentProcessing = ref(false)
const cancelling = ref(false)
const paymentSuccess = ref(false)
const errorMessage = ref(null)
const subscriptionStatus = ref({
  is_premium: false,
  subscription_expires_at: null,
  days_remaining: 0,
  is_on_trial: false,
  trial_ends_at: null,
  trial_days_remaining: 0
})
const subscriptionConfig = ref({
  price: '2.99',
  trial_days: 0
})
const user = ref(null)

const benefits = [
  'Acceso al contenido (VisionExpress)',
  'Acceso a JLHVISION RADIO online',
  'Contenido en alta calidad y Sin publicidad',
  'Guardar artículos en favoritos',
  'Actualizar tus datos'
]

const handleImageError = (event) => {
  event.target.src = '/avatar.png'
}

const loadUser = async () => {
  try {
    const { data } = await api.get('/app/user')
    if (data?.user) {
      user.value = data.user
      localStorage.setItem('user', JSON.stringify(data.user))
    }
  } catch (error) {
    const storedUser = localStorage.getItem('user')
    user.value = storedUser ? JSON.parse(storedUser) : { first_name: 'Usuario', email: '' }
  }
}

const initiatePayment = async () => {
  paymentProcessing.value = true
  errorMessage.value = null

  try {
    const { data } = await api.post('/subscription/checkout')
    if (data?.success && data?.checkout_url) {
      window.location.href = data.checkout_url
    } else {
      throw new Error(data?.error || 'No se pudo generar la sesión de pago')
    }
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Error al conectar con Stripe'
  } finally {
    paymentProcessing.value = false
  }
}

const loadSubscriptionStatus = async () => {
  try {
    const { data } = await api.get('/subscription/status')
    if (data?.success) {
      subscriptionStatus.value = data.data

      if (user.value) {
        user.value.is_premium = data.data.is_premium
        localStorage.setItem('user', JSON.stringify(user.value))
      }
    }
  } catch (error) {
    console.error('Error al cargar estado:', error)
  }
}

const loadSubscriptionConfig = async () => {
  try {
    const { data } = await api.get('/subscription/config')
    if (data?.success) {
      subscriptionConfig.value.premium = data.data.premium
    }
  } catch (error) {
    console.error('Error config:', error)
  }
}

/**
 * 🔥 CAMBIO AQUÍ: abre modal en vez de confirm()
 */
const cancelSubscription = () => {
  showCancelModal.value = true
}

/**
 * 🔥 NUEVO: confirmación real
 */
const confirmCancel = async () => {
  showCancelModal.value = false
  cancelling.value = true

  try {
    const { data } = await api.post('/subscription/cancel')
    if (data?.success) {
      showToast(data.message || 'Suscripción cancelada')
      await loadSubscriptionStatus()
    }
  } catch (error) {
    showToast(error.response?.data?.error || 'Error al cancelar', 'error')
  } finally {
    cancelling.value = false
  }
}

const checkPaymentCallback = () => {
  if (route.query.session_id) {
    paymentSuccess.value = true
    router.replace({ query: {} })

    setTimeout(() => {
      loadSubscriptionStatus()
      setTimeout(() => { paymentSuccess.value = false }, 5000)
    }, 3000)
  }

  if (route.query.payment_cancel) {
    errorMessage.value = "El proceso de pago fue cancelado."
    router.replace({ query: {} })
  }
}

const formatDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric', month: 'long', day: 'numeric'
  })
}

const goBack = () => {
  router.push('/mizona')
}

onMounted(async () => {
  loading.value = true
  await Promise.all([
    loadUser(),
    loadSubscriptionConfig(),
    loadSubscriptionStatus()
  ])
  checkPaymentCallback()
  loading.value = false
})
</script>

<style scoped>
.suscripcion-container {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.top-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.btn-back {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;
}

.btn-back:hover {
  background: #f0f0f0;
}

.btn-back i {
  font-size: 24px;
  color: #00144a;
}

.top-bar h1 {
  font-size: 20px;
  margin: 0;
  color: #00144a;
  font-weight: 600;
}

/* User Info Section */
.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  background: white;
  margin: 20px;
  padding: 16px;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-details h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  color: #00144a;
}

.user-details p {
  margin: 0;
  font-size: 14px;
  color: #6c757d;
}

/* Skeleton Loader Styles */
.skeleton {
  position: relative;
  overflow: hidden;
}

.skeleton::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent, 
    rgba(255,255,255,0.4), 
    transparent);
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeleton-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e0e0e0;
}

.skeleton-text {
  background: #e0e0e0;
  border-radius: 4px;
  height: 16px;
  margin: 8px 0;
}

.skeleton-title {
  width: 150px;
  height: 20px;
}

.skeleton-email {
  width: 180px;
  height: 14px;
}

.skeleton-container {
  margin: 20px;
}

.skeleton-card {
  padding: 24px;
}

.skeleton-badge {
  width: 120px;
  height: 32px;
  background: #e0e0e0;
  border-radius: 20px;
  margin-bottom: 20px;
}

.skeleton-heading {
  width: 250px;
  height: 28px;
  margin-bottom: 20px;
}

.skeleton-details {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin: 20px 0;
}

.skeleton-detail-item {
  height: 50px;
  background: #e0e0e0;
  margin: 10px 0;
  border-radius: 8px;
}

.skeleton-button {
  height: 48px;
  background: #e0e0e0;
  border-radius: 40px;
  margin: 20px 0;
}

.skeleton-benefits {
  margin-top: 20px;
}

.skeleton-benefit-item {
  height: 32px;
  background: #e0e0e0;
  margin: 8px 0;
  border-radius: 8px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top-color: #00144a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.card {
  background: #fff;
  border-radius: 16px;
  margin: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.premium-badge, .free-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 20px;
}

.premium-badge {
  background: #e8f5e9;
  color: #2e7d32;
}

.free-badge {
  background: #fff3e0;
  color: #e65100;
}

.premium h2, .free h2 {
  margin: 0 0 16px 0;
  font-size: 24px;
}

.premium h2 {
  color: #2e7d32;
}

.free h2 {
  color: #e65100;
}

.subscription-details {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin: 20px 0;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item i {
  font-size: 24px;
  color: #00144a;
}

.detail-item label {
  font-size: 12px;
  color: #6c757d;
  display: block;
}

.detail-item p {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.detail-item p.active {
  color: #2e7d32;
}

.detail-item p.warning {
  color: #f57c00;
}

.trial-info {
  background: #fff3e0;
  border-radius: 8px;
  padding: 12px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #e65100;
}

.pricing-card {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  margin: 20px 0;
  color: white;
}

.price {
  font-size: 48px;
  font-weight: 700;
}

.price .currency {
  font-size: 28px;
  vertical-align: super;
}

.price .period {
  font-size: 16px;
  font-weight: normal;
}

.trial-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255,255,255,0.2);
  padding: 8px 16px;
  border-radius: 20px;
  margin-top: 12px;
  font-size: 14px;
}

.btn-pay {
  width: 100%;
  background: linear-gradient(135deg, #4285f4 0%, #34a853 100%);
  color: white;
  border: none;
  padding: 14px 24px;
  border-radius: 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  margin: 20px 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-pay:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(66, 133, 244, 0.4);
}

.btn-pay:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-cancel {
  width: 100%;
  background: #fff;
  color: #dc3545;
  border: 1px solid #dc3545;
  padding: 12px 24px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin: 16px 0;
}

.btn-cancel:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.secure-payment {
  text-align: center;
  font-size: 12px;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.benefits-list {
  margin-top: 20px;
  padding: 0;
  list-style: none;
}

.benefits-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  color: #495057;
  font-size: 14px;
}

.benefits-list i {
  color: #34a853;
  font-size: 18px;
}

.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 12px;
  margin: 0 20px 20px 20px;
  animation: slideDown 0.3s ease;
  position: relative;
}

.alert.success {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  color: #2e7d32;
}

.alert.error {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  color: #c62828;
}

.alert i:first-child {
  font-size: 24px;
}

.alert p {
  margin: 0;
  flex: 1;
  font-size: 14px;
}

.close-alert {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.btn-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* MODAL */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal {
  background: white;
  padding: 24px;
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  animation: scaleIn 0.2s ease;
}

.modal h3 {
  margin-bottom: 10px;
  color: #00144a;
}

.modal p {
  font-size: 14px;
  color: #555;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  gap: 10px;
}

.btn-secondary {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: none;
  background: #e0e0e0;
  cursor: pointer;
}

.btn-danger {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: none;
  background: #dc3545;
  color: white;
  cursor: pointer;
}

.btn-danger:hover {
  background: #b02a37;
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .user-info {
    margin: 16px;
    padding: 12px;
  }
  
  .card {
    margin: 16px;
    padding: 20px;
  }
  
  .top-bar h1 {
    font-size: 18px;
  }
}

</style>